import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'
import { buildBruktbilQs } from '../qs'

type BaseParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

function baseParams(f: BaseParams) {
  return {
    from_date: f.from, to_date: f.to, klasse: f.klasse,
    varebil: f.varebil, omregistrering: f.omregistrering || undefined,
    ...(f.selgerPostnummer ? { selger_postnummer: f.selgerPostnummer.split(',') } : {}),
    ...(f.kjoperPostnummer ? { kjoper_postnummer: f.kjoperPostnummer.split(',') } : {}),
  }
}

export interface KlasseMerkeRow { merke: string; count: number; share_pct: number; topp_modell: string | null }
export interface KlasseMerkerResponse { class_label: string; rows: KlasseMerkeRow[]; total: number }

export interface KlasseModellRow { merke: string; modell: string; count: number; share_pct: number }
// total counts only the rows that carry a model, so share_pct sums to 100.
export interface KlasseModellerResponse { class_label: string; rows: KlasseModellRow[]; total: number }

export interface AlderYearBucket { aar: number; antall: number }
export interface KlasseAlderStats {
  snittalder: number | null; medianalder: number | null
  min_alder: number | null; max_alder: number | null; p25: number | null; p75: number | null
}
export interface AlderSeriesBucket { aar: number; antall: number; andel_pct: number }
export interface AlderSeries {
  merke: string; buckets: AlderSeriesBucket[]; over_25: number; over_25_andel_pct: number; total: number
  // Grid under the chart (#586 AC4) - exact over the real ages, so a brand
  // with many 25+ cars reads higher here than the clamped chart looks.
  snittalder: number | null; medianalder: number | null
}
export interface KlasseAldersprofilResponse {
  class_label: string; buckets: AlderYearBucket[]; over_25: number; stats: KlasseAlderStats; total: number
  series: AlderSeries[]
}

export interface KjoperAlderYearBucket { alder: number; antall: number }
export interface KjoperAlderSeriesBucket { alder: number; antall: number; andel_pct: number }
export interface KjoperAlderSeries {
  merke: string; buckets: KjoperAlderSeriesBucket[]; over_75: number; over_75_andel_pct: number; total: number
  // Grid under the chart (#586 AC3). Median is whole years; mean is not.
  snittalder: number | null; medianalder: number | null
}
export interface KlasseKjoperAlderResponse {
  class_label: string; buckets: KjoperAlderYearBucket[]; over_75: number
  snittalder: number | null; medianalder: number | null; total: number
  series: KjoperAlderSeries[]
  // False when the segment has no private buyers at all — the tab is then
  // hidden rather than rendered empty (#485 AC2).
  has_private_buyers: boolean
}

export interface KlasseDrivstoffRow { drivstoff: string; count: number; share_pct: number }
// One matrix column; merke is "Alle" for the whole-segment column (#586 AC5).
export interface KlasseDrivstoffSerie { merke: string; rows: KlasseDrivstoffRow[]; total: number }
export interface KlasseDrivstoffResponse {
  class_label: string; rows: KlasseDrivstoffRow[]; total: number
  series: KlasseDrivstoffSerie[]
}

export const fetchKlasseMerker = (classLabel: string, f: BaseParams) =>
  api.get<KlasseMerkerResponse>(`/bruktbil/klasser/${classLabel}/merker`, {
    params: { ...baseParams(f) }, paramsSerializer: { indexes: null },
  })
export const fetchKlasseModeller = (classLabel: string, f: BaseParams) =>
  api.get<KlasseModellerResponse>(`/bruktbil/klasser/${classLabel}/modeller`, {
    params: { ...baseParams(f) }, paramsSerializer: { indexes: null },
  })
export const fetchKlasseAldersprofil = (classLabel: string, f: BaseParams, merker: string[] = []) =>
  api.get<KlasseAldersprofilResponse>(
    `/bruktbil/klasser/${classLabel}/aldersprofil?${buildBruktbilQs(baseParams(f), merker)}`,
  )
export const fetchKlasseKjoperalder = (classLabel: string, f: BaseParams, merker: string[] = []) =>
  api.get<KlasseKjoperAlderResponse>(
    `/bruktbil/klasser/${classLabel}/kjoperalder?${buildBruktbilQs(baseParams(f), merker)}`,
  )
export const fetchKlasseDrivstoff = (classLabel: string, f: BaseParams, merker: string[] = []) =>
  api.get<KlasseDrivstoffResponse>(
    `/bruktbil/klasser/${classLabel}/drivstoff?${buildBruktbilQs(baseParams(f), merker)}`,
  )
